import Phaser from 'phaser';
import type { CommandCard } from '~shared/types';
import type { Side } from '~shared/types';
import { cardTextureKey } from '../scenes/PreloadScene';

// Desktop layout constants
const CARD_W   = 70;
const CARD_H   = 108;
const CARD_Y   = -16;
const CARD_GAP = 10;
const BTN_SIZE = 20;

const ZOOM_W = 168;
const ZOOM_H = 260;

// Desktop confirm dialog layout (panel 520×380 centred at 720,390)
const DLG_CX = 720;
const DLG_CY = 390;
const DLG_W  = 520;
const DLG_H  = 380;
const DLG_D  = 25; // base depth for dialog objects
const IMG_CX = 557; // card image centre-x inside dialog
const IMG_CY = 392; // card image centre-y
const IMG_W  = 155;
const IMG_H  = 240;
const TEXT_X = 648; // right-column left edge
const TEXT_W = 262; // word-wrap width for right column

interface Layout {
  cardW: number; cardH: number; cardY: number; cardGap: number;
  zoomW: number; zoomH: number; zoomCx: number; zoomCy: number;
  dlgCx: number; dlgCy: number; dlgW: number; dlgH: number;
  imgCx: number; imgCy: number; imgW: number; imgH: number;
  ruleVx: number; textX: number; textW: number;
  fontTitle: string; fontName: string; fontSection: string;
  fontRules: string; fontDesc: string; descLineSpacing: number;
  fontBtn: string; btnW: number; btnH: number; btnOffsetX: number;
}

// Touch layout — card-hand thumbnails fill the bottom strip (x250-1150, y622-768),
// the play-card dialog is near full-screen, and a separate big "zoom" preview
// (tap a card → big image, tap the image → open dialog) substitutes for the
// hover-zoom that desktop gets for free.
const TOUCH_LAYOUT: Layout = {
  cardW: 90, cardH: 139, cardY: -33, cardGap: 10,
  zoomW: 325, zoomH: 500, zoomCx: 700, zoomCy: 337,
  dlgCx: 720, dlgCy: 384, dlgW: 1320, dlgH: 660,
  imgCx: 260, imgCy: 384, imgW: 320, imgH: 496,
  ruleVx: 445, textX: 460, textW: 890,
  fontTitle: '22px', fontName: '26px', fontSection: '18px',
  fontRules: '15px', fontDesc: '19px', descLineSpacing: 7,
  fontBtn: '20px', btnW: 260, btnH: 60, btnOffsetX: 230,
};

const DESKTOP_LAYOUT: Layout = {
  cardW: CARD_W, cardH: CARD_H, cardY: CARD_Y, cardGap: CARD_GAP,
  zoomW: ZOOM_W, zoomH: ZOOM_H, zoomCx: 0, zoomCy: 0, // desktop positions the preview dynamically
  dlgCx: DLG_CX, dlgCy: DLG_CY, dlgW: DLG_W, dlgH: DLG_H,
  imgCx: IMG_CX, imgCy: IMG_CY, imgW: IMG_W, imgH: IMG_H,
  ruleVx: TEXT_X - 8, textX: TEXT_X, textW: TEXT_W,
  fontTitle: '14px', fontName: '16px', fontSection: '11px',
  fontRules: '9px', fontDesc: '12px', descLineSpacing: 4,
  fontBtn: '13px', btnW: 150, btnH: 36, btnOffsetX: 130,
};

export class CardHand extends Phaser.GameObjects.Container {
  private cards:        Phaser.GameObjects.Container[] = [];
  private canPlay       = false;
  private mySide:       Side;
  private onSelect:     ((cardId: string) => void) | null = null;
  private onInfo:       ((card: CommandCard | null) => void) | null = null;
  private onDialogToggle: ((visible: boolean) => void) | null = null;
  private activeInfoId: string | null = null;

  private isTouch: boolean;
  private L: Layout;

  // Hover zoom (desktop only)
  private zoomImg: Phaser.GameObjects.Image;

  // Confirm dialog
  private pendingCardId:   string | null = null;
  private dlgVisible       = false;
  private dlgOverlay!:     Phaser.GameObjects.Rectangle;
  private dlgPanel!:       Phaser.GameObjects.Rectangle;
  private dlgTitle!:       Phaser.GameObjects.Text;
  private dlgCardImg!:     Phaser.GameObjects.Image;
  private dlgCardName!:    Phaser.GameObjects.Text;
  private dlgCardSection!: Phaser.GameObjects.Text;
  private dlgRulesLbl!:    Phaser.GameObjects.Text;
  private dlgCardDesc!:    Phaser.GameObjects.Text;
  private dlgCancelBtn!:   Phaser.GameObjects.Rectangle;
  private dlgCancelLbl!:   Phaser.GameObjects.Text;
  private dlgPlayBtn!:     Phaser.GameObjects.Rectangle;
  private dlgPlayLbl!:     Phaser.GameObjects.Text;
  private dlgRuleH1!:      Phaser.GameObjects.Rectangle; // horizontal rule below title
  private dlgRuleV!:       Phaser.GameObjects.Rectangle; // vertical separator
  private dlgRuleH2!:      Phaser.GameObjects.Rectangle; // horizontal rule above buttons

  constructor(scene: Phaser.Scene, x: number, y: number, mySide: Side) {
    super(scene, x, y);
    this.mySide = mySide;
    this.isTouch = scene.sys.game.device.input.touch;
    this.L = this.isTouch ? TOUCH_LAYOUT : DESKTOP_LAYOUT;
    scene.add.existing(this);

    this.zoomImg = scene.add.image(0, 0, 'card-back')
      .setDisplaySize(this.L.zoomW, this.L.zoomH)
      .setVisible(false)
      .setDepth(20);

    this.buildDialog();
  }

  onCardSelected(cb: (cardId: string) => void): void { this.onSelect = cb; }
  onCardInfo(cb: (card: CommandCard | null) => void): void { this.onInfo = cb; }
  onDialogVisibilityChange(cb: (visible: boolean) => void): void { this.onDialogToggle = cb; }

  clearInfo(): void {
    this.activeInfoId = null;
    this.onInfo?.(null);
  }

  update(hand: CommandCard[], canPlay: boolean): void {
    this.canPlay = canPlay;
    this.activeInfoId = null;
    this.zoomImg.setVisible(false);
    if (this.dlgVisible) this.hideDialog();
    this.cards.forEach(c => c.destroy());
    this.cards = [];

    const { cardW, cardH, cardGap } = this.L;
    const totalW = hand.length * (cardW + cardGap) - cardGap;
    const startX = -totalW / 2;
    hand.forEach((card, i) => {
      const cx = startX + i * (cardW + cardGap) + cardW / 2;
      this.cards.push(this.buildCard(card, cx, canPlay));
    });
  }

  // ── Dialog ────────────────────────────────────────────────────────────

  private buildDialog(): void {
    const s = this.scene;
    const L = this.L;
    const top    = L.dlgCy - L.dlgH / 2;
    const bottom = L.dlgCy + L.dlgH / 2;

    const titleY       = top    + (this.isTouch ? 38  : 22);
    const ruleH1Y      = top    + (this.isTouch ? 66  : 38);
    const cardNameY    = top    + (this.isTouch ? 97  : 56);
    const cardSectionY = top    + (this.isTouch ? 167 : 96);
    const rulesLblY    = top    + (this.isTouch ? 205 : 118);
    const cardDescY    = top    + (this.isTouch ? 229 : 132);
    const ruleH2Y      = bottom - (this.isTouch ? 90  : 52);
    const btnY         = bottom - (this.isTouch ? 52  : 30);
    const ruleVH       = L.dlgH - (this.isTouch ? 60  : 70);
    const ruleVY       = L.dlgCy + (this.isTouch ? 0   : 10);

    // Full-screen dimmer — blocks all clicks on the game behind it
    this.dlgOverlay = s.add.rectangle(720, 384, 1440, 768, 0x000000, 0.65)
      .setDepth(DLG_D).setVisible(false).setInteractive();

    // Panel
    this.dlgPanel = s.add.rectangle(L.dlgCx, L.dlgCy, L.dlgW, L.dlgH, 0x0a1a0a)
      .setStrokeStyle(2, 0xd4af37, 1)
      .setDepth(DLG_D + 1).setVisible(false);

    // Title
    this.dlgTitle = s.add.text(L.dlgCx, titleY, 'PLAY COMMAND CARD?', {
      fontSize: L.fontTitle, color: '#d4af37', fontStyle: 'bold',
    }).setOrigin(0.5, 0.5).setDepth(DLG_D + 2).setVisible(false);

    // Thin rule below title
    this.dlgRuleH1 = s.add.rectangle(L.dlgCx, ruleH1Y, L.dlgW - 20, 1, 0x334433)
      .setDepth(DLG_D + 2).setVisible(false);

    // Card image (left column)
    this.dlgCardImg = s.add.image(L.imgCx, L.imgCy, 'card-back')
      .setDisplaySize(L.imgW, L.imgH)
      .setDepth(DLG_D + 2).setVisible(false);

    // Thin vertical separator
    this.dlgRuleV = s.add.rectangle(L.ruleVx, ruleVY, 1, ruleVH, 0x334433)
      .setDepth(DLG_D + 2).setVisible(false);

    // Right column: card name
    this.dlgCardName = s.add.text(L.textX, cardNameY, '', {
      fontSize: L.fontName, color: '#ffffff', fontStyle: 'bold',
      wordWrap: { width: L.textW },
    }).setDepth(DLG_D + 2).setVisible(false);

    // Section + orders
    this.dlgCardSection = s.add.text(L.textX, cardSectionY, '', {
      fontSize: L.fontSection, color: '#88ddaa',
    }).setDepth(DLG_D + 2).setVisible(false);

    // Rules label
    this.dlgRulesLbl = s.add.text(L.textX, rulesLblY, 'RULES:', {
      fontSize: L.fontRules, color: '#667766', fontStyle: 'bold',
    }).setDepth(DLG_D + 2).setVisible(false);

    // Description
    this.dlgCardDesc = s.add.text(L.textX, cardDescY, '', {
      fontSize: L.fontDesc, color: '#ccddbb',
      wordWrap: { width: L.textW }, lineSpacing: L.descLineSpacing,
    }).setDepth(DLG_D + 2).setVisible(false);

    // Thin rule above buttons
    this.dlgRuleH2 = s.add.rectangle(L.dlgCx, ruleH2Y, L.dlgW - 20, 1, 0x334433)
      .setDepth(DLG_D + 2).setVisible(false);

    // Cancel button
    this.dlgCancelBtn = s.add.rectangle(L.dlgCx - L.btnOffsetX, btnY, L.btnW, L.btnH, 0x3a2020)
      .setStrokeStyle(1, 0x886655, 1)
      .setDepth(DLG_D + 2).setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => this.dlgCancelBtn.setFillStyle(0x5a3030))
      .on('pointerout',  () => this.dlgCancelBtn.setFillStyle(0x3a2020))
      .on('pointerdown', () => this.hideDialog());

    this.dlgCancelLbl = s.add.text(L.dlgCx - L.btnOffsetX, btnY, 'Cancel', {
      fontSize: L.fontBtn, color: '#ddaaaa', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DLG_D + 3).setVisible(false);

    // Play Card button
    this.dlgPlayBtn = s.add.rectangle(L.dlgCx + L.btnOffsetX, btnY, L.btnW, L.btnH, 0x1a3a22)
      .setStrokeStyle(1, 0x55bb77, 1)
      .setDepth(DLG_D + 2).setVisible(false)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => this.dlgPlayBtn.setFillStyle(0x2a5a33))
      .on('pointerout',  () => this.dlgPlayBtn.setFillStyle(0x1a3a22))
      .on('pointerdown', () => {
        if (this.pendingCardId) this.onSelect?.(this.pendingCardId);
        this.hideDialog();
      });

    this.dlgPlayLbl = s.add.text(L.dlgCx + L.btnOffsetX, btnY, 'Play Card', {
      fontSize: L.fontBtn, color: '#aaffbb', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DLG_D + 3).setVisible(false);
  }

  private showDialog(card: CommandCard): void {
    this.pendingCardId = card.id;
    this.zoomImg.setVisible(false);

    this.dlgCardImg.setTexture(cardTextureKey(card.id, this.mySide));
    this.dlgCardName.setText(card.name);

    const orders = card.unitsToOrder === 0
      ? 'All in section'
      : card.unitsToOrder === 9
        ? 'All armor'
        : `${card.unitsToOrder} unit${card.unitsToOrder !== 1 ? 's' : ''}`;
    this.dlgCardSection.setText(`[${card.section.toUpperCase()} FLANK]   Orders: ${orders}`);
    this.dlgCardDesc.setText(card.description);

    this.dlgOverlay.setVisible(true);
    this.dlgPanel.setVisible(true);
    this.dlgTitle.setVisible(true);
    this.dlgRuleH1.setVisible(true);
    this.dlgRuleV.setVisible(true);
    this.dlgRuleH2.setVisible(true);
    this.dlgCardImg.setVisible(true);
    this.dlgCardName.setVisible(true);
    this.dlgCardSection.setVisible(true);
    this.dlgRulesLbl.setVisible(true);
    this.dlgCardDesc.setVisible(true);
    this.dlgCancelBtn.setVisible(true);
    this.dlgCancelLbl.setVisible(true);
    this.dlgPlayBtn.setVisible(true);
    this.dlgPlayLbl.setVisible(true);
    this.dlgVisible = true;
    this.onDialogToggle?.(true);
  }

  private hideDialog(): void {
    this.pendingCardId = null;
    this.dlgVisible = false;
    this.onDialogToggle?.(false);
    this.dlgOverlay.setVisible(false);
    this.dlgPanel.setVisible(false);
    this.dlgTitle.setVisible(false);
    this.dlgRuleH1.setVisible(false);
    this.dlgRuleV.setVisible(false);
    this.dlgRuleH2.setVisible(false);
    this.dlgCardImg.setVisible(false);
    this.dlgCardName.setVisible(false);
    this.dlgCardSection.setVisible(false);
    this.dlgRulesLbl.setVisible(false);
    this.dlgCardDesc.setVisible(false);
    this.dlgCancelBtn.setVisible(false).setFillStyle(0x3a2020);
    this.dlgCancelLbl.setVisible(false);
    this.dlgPlayBtn.setVisible(false).setFillStyle(0x1a3a22);
    this.dlgPlayLbl.setVisible(false);
  }

  // ── Card building ─────────────────────────────────────────────────────

  private buildCard(card: CommandCard, cx: number, interactive: boolean): Phaser.GameObjects.Container {
    const { cardW, cardH, cardY } = this.L;
    const c = this.scene.add.container(cx, 0);
    this.add(c);

    const border = this.scene.add.rectangle(0, cardY, cardW + 6, cardH + 6, 0x000000, 0);
    border.setStrokeStyle(3, 0xd4af37, 0);

    const key = cardTextureKey(card.id, this.mySide);
    const img = this.scene.add.image(0, cardY, key)
      .setDisplaySize(cardW, cardH)
      .setAlpha(interactive ? 1 : 0.35);

    c.add([border, img]);

    if (!interactive) return c;

    img.setInteractive({ useHandCursor: true });

    if (this.isTouch) {
      // Touch: no hover zoom, no "?" button — tap opens the (large) confirm dialog directly
      img.on('pointerdown', () => {
        if (!this.canPlay) return;
        this.showDialog(card);
      });
    } else {
      // Desktop: hover zoom + "?" info button in corner
      const btnSz  = BTN_SIZE;
      const btnX   = cardW / 2 - btnSz / 2 - 1;
      const btnY   = cardY - cardH / 2 + btnSz / 2 + 1;
      const infoBox = this.scene.add.rectangle(btnX, btnY, btnSz, btnSz, 0x1a3322,
        interactive ? 0.88 : 0)
        .setStrokeStyle(1.5, 0x55bb77, interactive ? 1 : 0);
      const infoLbl = this.scene.add.text(btnX, btnY, '?', {
        fontSize: '12px', color: '#88ffaa', fontStyle: 'bold',
      }).setOrigin(0.5).setAlpha(interactive ? 1 : 0);
      c.add([infoBox, infoLbl]);

      img.on('pointerover', () => {
        img.setAlpha(0.85);
        if (this.activeInfoId !== card.id) border.setStrokeStyle(3, 0xffffff, 0.6);
        this.showZoom(card.id, cx);
      });
      img.on('pointerout', () => {
        img.setAlpha(1);
        if (this.activeInfoId !== card.id) border.setStrokeStyle(3, 0xd4af37, 0);
        this.zoomImg.setVisible(false);
      });
      img.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (!this.canPlay) return;
        const btnWorldX = this.x + cx + btnX;
        const btnWorldY = this.y + btnY;
        const onBtn = Math.abs(pointer.x - btnWorldX) <= btnSz / 2 &&
                      Math.abs(pointer.y - btnWorldY) <= btnSz / 2;
        if (onBtn) {
          if (this.activeInfoId === card.id) {
            this.activeInfoId = null;
            border.setStrokeStyle(3, 0xd4af37, 0);
            infoBox.setFillStyle(0x1a3322);
            infoLbl.setColor('#88ffaa');
            this.onInfo?.(null);
          } else {
            this.activeInfoId = card.id;
            border.setStrokeStyle(3, 0xd4af37, 1);
            infoBox.setFillStyle(0x33bb55);
            infoLbl.setColor('#ffffff');
            this.onInfo?.(card);
          }
        } else {
          this.showDialog(card);
        }
      });
    }

    return c;
  }

  private showZoom(cardId: string, cx: number): void {
    const worldX  = this.x + cx;
    const cardTopY = this.y + this.L.cardY - this.L.cardH / 2;
    const zoomX   = Math.max(this.L.zoomW / 2 + 5, Math.min(1168 - this.L.zoomW / 2 - 5, worldX));
    const zoomY   = cardTopY - this.L.zoomH / 2 - 12;

    this.zoomImg
      .setPosition(zoomX, zoomY)
      .setTexture(cardTextureKey(cardId, this.mySide))
      .setVisible(true);
  }
}
